import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Input,
  InputNumber,
  Row,
  Segmented,
  Select,
  Slider,
  Space,
  Typography,
  message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import dayjs from 'dayjs';
import api from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { usePermission } from '../../hooks/usePermission';
import {
  ACCOUNT_TENURES,
  ACCOUNT_TIERS,
  CALL_TYPE_OPTIONS,
  CONTACT_METHODS,
  ESCALATION_TYPES,
  OFFER_OPTIONS,
  RETAIN_IQ_OUTCOMES,
  RETAIN_IQ_TOPICS,
  UPSELL_PRODUCTS,
} from './constants';
import './retainiq.css';

const { Title, Text } = Typography;
const { TextArea } = Input;

type Payload = Record<string, unknown>;

const emptyPayload = (): Payload => ({
  call_date: dayjs().format('YYYY-MM-DD'),
  call_time: dayjs().format('HH:mm'),
  call_duration: '',
  call_type: '',
  contact_method: 'Phone Call',
  contact_made: 'Reached',
  merchant_id: '',
  contact_name: '',
  contact_title: '',
  contact_phone: '',
  contact_email: '',
  account_tier: '',
  account_tenure: '',
  risk_level: '',
  call_reason: '',
  topics: [] as string[],
  sentiment: '',
  pain_points: '',
  positive_notes: '',
  commitments: '',
  upsell_flag: 'No',
  upsell_product: '',
  escalation_flag: 'No',
  escalation_type: '',
  offer_made: '',
  offer_value: '',
  followup_flag: 'No',
  followup_date: '',
  followup_action: '',
  call_outcome: '',
  nps_score: 5,
  call_notes: '',
});

export const RetainIQCallLog: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canViewAllBusinesses = usePermission('view_all_businesses');
  const canAssign =
    user?.role === 'admin' ||
    user?.role === 'agent' ||
    user?.role === 'developer' ||
    canViewAllBusinesses;

  const [businessId, setBusinessId] = useState<string | undefined>();
  const [businessSearch, setBusinessSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [agentUserId, setAgentUserId] = useState<string | undefined>();
  const [payload, setPayload] = useState<Payload>(emptyPayload);
  const [createTicket, setCreateTicket] = useState(false);
  const [linkTicketId, setLinkTicketId] = useState('');
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketType, setTicketType] = useState<string | undefined>();
  const [ticketCategoryId, setTicketCategoryId] = useState<string | undefined>();
  const [ticketAssignee, setTicketAssignee] = useState<string | undefined>();
  const [ticketPriority, setTicketPriority] = useState('medium');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(businessSearch), 300);
    return () => clearTimeout(t);
  }, [businessSearch]);

  const { data: bizRes } = useQuery(
    ['retain-iq-businesses', debouncedSearch],
    () =>
      api
        .get(`/api/businesses/search?search=${encodeURIComponent(debouncedSearch)}&limit=200`)
        .then((r) => r.data.businesses || []),
    { staleTime: 30_000 }
  );
  const businesses = bizRes || [];

  const { data: agentsRes } = useQuery('retain-iq-agents', () =>
    api.get('/api/retain-iq/agents').then((r) => r.data.agents || [])
  );
  const { data: settingsRes } = useQuery('retain-iq-settings', () =>
    api.get('/api/retain-iq/settings').then((r) => r.data)
  );

  const { data: categories = [] } = useQuery('retain-iq-categories', () =>
    api.get('/api/categories').then((r) => (Array.isArray(r.data?.categories) ? r.data.categories : []))
  );

  const { data: ticketTypes = [] } = useQuery('retain-iq-ticket-types', () =>
    api.get('/api/ticket-types').then((r) => (Array.isArray(r.data?.types) ? r.data.types : []))
  );

  useEffect(() => {
    if (!user?.id || !agentsRes?.length || agentUserId) return;
    const me = agentsRes.find((a: { id: string }) => String(a.id) === String(user.id));
    if (me) setAgentUserId(String(me.id));
  }, [user?.id, agentsRes, agentUserId]);

  useEffect(() => {
    const def = settingsRes?.default_ticket_category_id;
    if (def && !ticketCategoryId) {
      setTicketCategoryId(String(def));
    }
  }, [settingsRes, ticketCategoryId]);

  const saveMutation = useMutation(
    async () => {
      if (!businessId) throw new Error('Select a business');
      if (!agentUserId) throw new Error('Select an agent');
      if (!String(payload.call_type || '').trim()) throw new Error('Call type is required');
      const body: Record<string, unknown> = {
        business_id: businessId,
        agent_user_id: agentUserId,
        payload,
      };
      const link = linkTicketId.trim();
      if (link) {
        body.ticket_id = link;
      }
      if (createTicket) {
        body.create_ticket = true;
        body.ticket = {
          subject: ticketSubject.trim() || undefined,
          type: ticketType,
          category_id: ticketCategoryId,
          assigned_to: ticketAssignee || null,
          priority: ticketPriority,
        };
      }
      const res = await api.post('/api/retain-iq/logs', body);
      return res.data;
    },
    {
      onSuccess: () => {
        message.success('Call log saved');
        queryClient.invalidateQueries('retain-iq-reports');
        queryClient.invalidateQueries('retain-iq-logs');
        setPayload(emptyPayload());
        setLinkTicketId('');
        setCreateTicket(false);
        setTicketSubject('');
      },
      onError: (e: unknown) => {
        const err = e as { response?: { data?: { error?: string } } };
        message.error(err.response?.data?.error || 'Save failed');
      },
    }
  );

  const flatCallTypes = useMemo(
    () => CALL_TYPE_OPTIONS.flatMap((g) => g.options.map((o) => ({ label: `${g.label}: ${o.label}`, value: o.value }))),
    []
  );

  const toggleTopic = (topic: string, checked: boolean) => {
    const cur = (payload.topics as string[]) || [];
    if (checked) setPayload({ ...payload, topics: [...cur, topic] });
    else setPayload({ ...payload, topics: cur.filter((t) => t !== topic) });
  };

  const nps = Number(payload.nps_score) || 0;
  const npsLabel = nps >= 9 ? 'Promoter' : nps >= 7 ? 'Passive' : 'Detractor';

  return (
    <div>
      <div className="retain-iq-no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Call log form
          </Title>
          <Text type="secondary">Log every interaction — stay consistent, stay compliant</Text>
        </div>
        <Space>
          <Button
            onClick={() => {
              setPayload(emptyPayload());
              setLinkTicketId('');
              setCreateTicket(false);
            }}
          >
            Clear
          </Button>
          <Button onClick={() => window.print()}>Print</Button>
          <Button type="primary" loading={saveMutation.isLoading} onClick={() => saveMutation.mutate()}>
            Save call log
          </Button>
        </Space>
      </div>

      <div className="retain-iq-print-area">
        <Card title="Call information" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Text type="secondary">Date</Text>
              <DatePicker
                style={{ width: '100%', marginTop: 6 }}
                value={payload.call_date ? dayjs(String(payload.call_date)) : null}
                onChange={(d) => setPayload({ ...payload, call_date: d ? d.format('YYYY-MM-DD') : '' })}
              />
            </Col>
            <Col xs={24} md={8}>
              <Text type="secondary">Time</Text>
              <Input
                style={{ marginTop: 6 }}
                type="time"
                value={String(payload.call_time || '')}
                onChange={(e) => setPayload({ ...payload, call_time: e.target.value })}
              />
            </Col>
            <Col xs={24} md={8}>
              <Text type="secondary">Duration (min)</Text>
              <InputNumber
                style={{ width: '100%', marginTop: 6 }}
                min={0}
                value={payload.call_duration === '' ? undefined : Number(payload.call_duration)}
                onChange={(v) => setPayload({ ...payload, call_duration: v == null ? '' : String(v) })}
              />
            </Col>
            <Col xs={24} md={8} style={{ marginTop: 16 }}>
              <Text type="secondary">Agent</Text>
              <Select
                showSearch
                optionFilterProp="label"
                style={{ width: '100%', marginTop: 6 }}
                placeholder="Agent"
                value={agentUserId}
                onChange={setAgentUserId}
                options={(agentsRes || []).map((a: { id: string; name: string; level_label?: string }) => ({
                  value: a.id,
                  label: `${a.name}${a.level_label ? ` — ${a.level_label}` : ''}`,
                }))}
              />
            </Col>
            <Col xs={24} md={8} style={{ marginTop: 16 }}>
              <Text type="secondary">Call type</Text>
              <Select
                showSearch
                style={{ width: '100%', marginTop: 6 }}
                placeholder="Select call type"
                value={payload.call_type || undefined}
                onChange={(v) => setPayload({ ...payload, call_type: v })}
                options={flatCallTypes}
              />
            </Col>
            <Col xs={24} md={8} style={{ marginTop: 16 }}>
              <Text type="secondary">Contact method</Text>
              <Select
                style={{ width: '100%', marginTop: 6 }}
                value={String(payload.contact_method || 'Phone Call')}
                onChange={(v) => setPayload({ ...payload, contact_method: v })}
                options={CONTACT_METHODS.map((c) => ({ value: c, label: c }))}
              />
            </Col>
            <Col span={24} style={{ marginTop: 16 }}>
              <Text type="secondary">Contact made?</Text>
              <div style={{ marginTop: 8 }}>
                <Segmented
                  value={String(payload.contact_made || 'Reached')}
                  onChange={(v) => setPayload({ ...payload, contact_made: String(v) })}
                  options={[
                    { label: 'Reached', value: 'Reached' },
                    { label: 'Voicemail', value: 'Voicemail' },
                    { label: 'No answer', value: 'No Answer' },
                  ]}
                />
              </div>
            </Col>
          </Row>
        </Card>

        <Card title="Account details" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Text type="secondary">Business</Text>
              <Select
                showSearch
                filterOption={false}
                onSearch={setBusinessSearch}
                style={{ width: '100%', marginTop: 6 }}
                placeholder="Search businesses"
                value={businessId}
                onChange={(id: string) => {
                  setBusinessId(id);
                  const b = businesses.find((x: { id: string }) => x.id === id);
                  if (b?.business_name) setBusinessSearch(b.business_name);
                }}
                options={businesses.map((b: { id: string; business_name?: string; name?: string }) => ({
                  value: b.id,
                  label: b.business_name || b.name || b.id,
                }))}
              />
            </Col>
            <Col xs={24} md={12}>
              <Text type="secondary">Merchant ID / Account #</Text>
              <Input
                style={{ marginTop: 6 }}
                value={String(payload.merchant_id || '')}
                onChange={(e) => setPayload({ ...payload, merchant_id: e.target.value })}
              />
            </Col>
            <Col xs={24} md={12} style={{ marginTop: 12 }}>
              <Text type="secondary">Contact name</Text>
              <Input
                style={{ marginTop: 6 }}
                value={String(payload.contact_name || '')}
                onChange={(e) => setPayload({ ...payload, contact_name: e.target.value })}
              />
            </Col>
            <Col xs={24} md={12} style={{ marginTop: 12 }}>
              <Text type="secondary">Contact title</Text>
              <Input
                style={{ marginTop: 6 }}
                value={String(payload.contact_title || '')}
                onChange={(e) => setPayload({ ...payload, contact_title: e.target.value })}
              />
            </Col>
            <Col xs={24} md={12} style={{ marginTop: 12 }}>
              <Text type="secondary">Phone</Text>
              <Input
                style={{ marginTop: 6 }}
                value={String(payload.contact_phone || '')}
                onChange={(e) => setPayload({ ...payload, contact_phone: e.target.value })}
              />
            </Col>
            <Col xs={24} md={12} style={{ marginTop: 12 }}>
              <Text type="secondary">Email</Text>
              <Input
                style={{ marginTop: 6 }}
                value={String(payload.contact_email || '')}
                onChange={(e) => setPayload({ ...payload, contact_email: e.target.value })}
              />
            </Col>
            <Col xs={24} md={12} style={{ marginTop: 12 }}>
              <Text type="secondary">Account tier</Text>
              <Select
                allowClear
                style={{ width: '100%', marginTop: 6 }}
                value={payload.account_tier || undefined}
                onChange={(v) => setPayload({ ...payload, account_tier: v || '' })}
                options={ACCOUNT_TIERS.filter(Boolean).map((t) => ({ value: t, label: t }))}
              />
            </Col>
            <Col xs={24} md={12} style={{ marginTop: 12 }}>
              <Text type="secondary">Account tenure</Text>
              <Select
                allowClear
                style={{ width: '100%', marginTop: 6 }}
                value={payload.account_tenure || undefined}
                onChange={(v) => setPayload({ ...payload, account_tenure: v || '' })}
                options={ACCOUNT_TENURES.filter(Boolean).map((t) => ({ value: t, label: t }))}
              />
            </Col>
            <Col span={24} style={{ marginTop: 16 }}>
              <Text type="secondary">Risk level</Text>
              <div style={{ marginTop: 8 }}>
                <Segmented
                  value={payload.risk_level ? String(payload.risk_level) : undefined}
                  onChange={(v) => setPayload({ ...payload, risk_level: v ? String(v) : '' })}
                  options={[
                    { label: 'Low', value: 'low' },
                    { label: 'Medium', value: 'medium' },
                    { label: 'High', value: 'high' },
                    { label: 'Critical', value: 'critical' },
                  ]}
                />
              </div>
            </Col>
          </Row>
        </Card>

        <Card title="Discussion summary" style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div>
              <Text type="secondary">Reason / purpose of call</Text>
              <Input
                style={{ marginTop: 6 }}
                value={String(payload.call_reason || '')}
                onChange={(e) => setPayload({ ...payload, call_reason: e.target.value })}
              />
            </div>
            <div>
              <Text type="secondary">Topics discussed</Text>
              <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {RETAIN_IQ_TOPICS.map((topic) => (
                  <Checkbox
                    key={topic}
                    checked={((payload.topics as string[]) || []).includes(topic)}
                    onChange={(e) => toggleTopic(topic, e.target.checked)}
                  >
                    {topic}
                  </Checkbox>
                ))}
              </div>
            </div>
            <div>
              <Text type="secondary">Customer sentiment</Text>
              <div style={{ marginTop: 8 }}>
                <Segmented
                  value={payload.sentiment ? String(payload.sentiment) : undefined}
                  onChange={(v) => setPayload({ ...payload, sentiment: v ? String(v) : '' })}
                  options={[
                    { label: 'Very +', value: 'Very Positive' },
                    { label: 'Positive', value: 'Positive' },
                    { label: 'Neutral', value: 'Neutral' },
                    { label: 'Negative', value: 'Negative' },
                    { label: 'Very −', value: 'Very Negative' },
                  ]}
                />
              </div>
            </div>
            <Row gutter={16}>
              <Col span={24} md={12}>
                <Text type="secondary">Pain points / issues</Text>
                <TextArea
                  style={{ marginTop: 6 }}
                  rows={3}
                  value={String(payload.pain_points || '')}
                  onChange={(e) => setPayload({ ...payload, pain_points: e.target.value })}
                />
              </Col>
              <Col span={24} md={12}>
                <Text type="secondary">Key wins / positive feedback</Text>
                <TextArea
                  style={{ marginTop: 6 }}
                  rows={3}
                  value={String(payload.positive_notes || '')}
                  onChange={(e) => setPayload({ ...payload, positive_notes: e.target.value })}
                />
              </Col>
            </Row>
            <div>
              <Text type="secondary">Commitments made</Text>
              <TextArea
                style={{ marginTop: 6 }}
                rows={2}
                value={String(payload.commitments || '')}
                onChange={(e) => setPayload({ ...payload, commitments: e.target.value })}
              />
            </div>
          </Space>
        </Card>

        <Card title="Opportunities & escalations" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            <Col span={24} md={12}>
              <Text type="secondary">Upsell opportunity?</Text>
              <div style={{ marginTop: 8 }}>
                <Segmented
                  value={String(payload.upsell_flag || 'No')}
                  onChange={(v) => setPayload({ ...payload, upsell_flag: String(v) })}
                  options={[
                    { label: 'No', value: 'No' },
                    { label: 'Yes', value: 'Yes' },
                  ]}
                />
              </div>
              {payload.upsell_flag === 'Yes' && (
                <Select
                  style={{ width: '100%', marginTop: 8 }}
                  placeholder="Product / service"
                  value={payload.upsell_product || undefined}
                  onChange={(v) => setPayload({ ...payload, upsell_product: v || '' })}
                  options={UPSELL_PRODUCTS.filter(Boolean).map((x) => ({ value: x, label: x }))}
                />
              )}
            </Col>
            <Col span={24} md={12}>
              <Text type="secondary">Escalation needed?</Text>
              <div style={{ marginTop: 8 }}>
                <Segmented
                  value={String(payload.escalation_flag || 'No')}
                  onChange={(v) => setPayload({ ...payload, escalation_flag: String(v) })}
                  options={[
                    { label: 'No', value: 'No' },
                    { label: 'Yes', value: 'Yes' },
                  ]}
                />
              </div>
              {payload.escalation_flag === 'Yes' && (
                <Select
                  style={{ width: '100%', marginTop: 8 }}
                  placeholder="Escalation type"
                  value={payload.escalation_type || undefined}
                  onChange={(v) => setPayload({ ...payload, escalation_type: v || '' })}
                  options={ESCALATION_TYPES.filter(Boolean).map((x) => ({ value: x, label: x }))}
                />
              )}
            </Col>
            <Col span={24} md={12}>
              <Text type="secondary">Offer made / retention action</Text>
              <Select
                allowClear
                style={{ width: '100%', marginTop: 6 }}
                value={payload.offer_made || undefined}
                onChange={(v) => setPayload({ ...payload, offer_made: v || '' })}
                options={OFFER_OPTIONS.filter(Boolean).map((x) => ({ value: x, label: x }))}
              />
            </Col>
            <Col span={24} md={12}>
              <Text type="secondary">Offer value ($)</Text>
              <InputNumber
                style={{ width: '100%', marginTop: 6 }}
                min={0}
                value={payload.offer_value === '' ? undefined : Number(payload.offer_value)}
                onChange={(v) => setPayload({ ...payload, offer_value: v == null ? '' : String(v) })}
              />
            </Col>
          </Row>
        </Card>

        <Card title="Follow-up & outcome" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Text type="secondary">Follow-up required?</Text>
              <div style={{ marginTop: 8 }}>
                <Segmented
                  value={String(payload.followup_flag || 'No')}
                  onChange={(v) => setPayload({ ...payload, followup_flag: String(v) })}
                  options={[
                    { label: 'No', value: 'No' },
                    { label: 'Yes', value: 'Yes' },
                  ]}
                />
              </div>
            </Col>
            {payload.followup_flag === 'Yes' && (
              <>
                <Col xs={24} md={12}>
                  <Text type="secondary">Follow-up date</Text>
                  <DatePicker
                    style={{ width: '100%', marginTop: 6 }}
                    value={payload.followup_date ? dayjs(String(payload.followup_date)) : null}
                    onChange={(d) => setPayload({ ...payload, followup_date: d ? d.format('YYYY-MM-DD') : '' })}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <Text type="secondary">Follow-up action / owner</Text>
                  <Input
                    style={{ marginTop: 6 }}
                    value={String(payload.followup_action || '')}
                    onChange={(e) => setPayload({ ...payload, followup_action: e.target.value })}
                  />
                </Col>
              </>
            )}
            <Col span={24}>
              <Text type="secondary">Overall call outcome</Text>
              <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {RETAIN_IQ_OUTCOMES.map((o) => (
                  <Button
                    key={o}
                    type={payload.call_outcome === o ? 'primary' : 'default'}
                    size="small"
                    onClick={() => setPayload({ ...payload, call_outcome: o })}
                  >
                    {o}
                  </Button>
                ))}
              </div>
            </Col>
            <Col span={24}>
              <Text type="secondary">NPS (0–10)</Text>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8 }}>
                <Slider
                  min={0}
                  max={10}
                  style={{ flex: 1 }}
                  value={nps}
                  onChange={(v) => setPayload({ ...payload, nps_score: v })}
                />
                <Text strong>
                  {nps} / 10 — {npsLabel}
                </Text>
              </div>
            </Col>
            <Col span={24}>
              <Text type="secondary">Additional notes</Text>
              <TextArea
                style={{ marginTop: 6 }}
                rows={4}
                value={String(payload.call_notes || '')}
                onChange={(e) => setPayload({ ...payload, call_notes: e.target.value })}
              />
            </Col>
          </Row>
        </Card>

        <Card title="Ticket (optional)" style={{ marginBottom: 16 }} className="retain-iq-no-print">
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div>
              <Text type="secondary">Link existing ticket ID</Text>
              <Input
                style={{ marginTop: 6, maxWidth: 400 }}
                placeholder="UUID of existing ticket"
                value={linkTicketId}
                onChange={(e) => setLinkTicketId(e.target.value)}
                disabled={createTicket}
              />
            </div>
            <Checkbox checked={createTicket} onChange={(e) => setCreateTicket(e.target.checked)} disabled={!!linkTicketId.trim()}>
              Create support ticket (unassigned pool when assignee cleared)
            </Checkbox>
            {createTicket && (
              <Row gutter={16} style={{ width: '100%' }}>
                <Col xs={24} md={12}>
                  <Text type="secondary">Category</Text>
                  <Select
                    showSearch
                    optionFilterProp="children"
                    style={{ width: '100%', marginTop: 6 }}
                    placeholder="Category"
                    value={ticketCategoryId}
                    onChange={(v) => setTicketCategoryId(v)}
                    options={categories.map((c: { id: string; name: string }) => ({ value: c.id, label: c.name }))}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <Text type="secondary">Type</Text>
                  <Select
                    showSearch
                    style={{ width: '100%', marginTop: 6 }}
                    placeholder="Ticket type"
                    value={ticketType}
                    onChange={setTicketType}
                    options={ticketTypes
                      .filter((t: { is_active?: boolean }) => t.is_active !== false)
                      .map((t: { id: string; name: string }) => ({ value: t.name, label: t.name }))}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <Text type="secondary">Priority</Text>
                  <Select
                    style={{ width: '100%', marginTop: 6 }}
                    value={ticketPriority}
                    onChange={setTicketPriority}
                    options={[
                      { value: 'low', label: 'Low' },
                      { value: 'medium', label: 'Medium' },
                      { value: 'high', label: 'High' },
                    ]}
                  />
                </Col>
                {canAssign && (
                  <Col xs={24} md={12}>
                    <Text type="secondary">Assignee (optional)</Text>
                    <Select
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      style={{ width: '100%', marginTop: 6 }}
                      placeholder="Unassigned"
                      value={ticketAssignee}
                      onChange={setTicketAssignee}
                      options={(agentsRes || []).map((a: { id: string; name: string }) => ({ value: a.id, label: a.name }))}
                    />
                  </Col>
                )}
                <Col span={24}>
                  <Text type="secondary">Subject override</Text>
                  <Input
                    style={{ marginTop: 6 }}
                    placeholder="Defaults from call type + business"
                    value={ticketSubject}
                    onChange={(e) => setTicketSubject(e.target.value)}
                  />
                </Col>
              </Row>
            )}
          </Space>
        </Card>
      </div>
    </div>
  );
};
